import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { IGridDataService } from '@interfaces/grid-data-service.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { TabCounterService, TabCounterType } from '@services/tab-counter.service';
import { lastValueFrom } from 'rxjs';
import {
    ChangeOpportunityStatusResultDto,
    CreateOpportunityManualParameters,
    DomainValidationResult,
    EditOpportunityView,
    GetOpportunityActualValuesResultDto,
    GetOpportunityTermResultDto,
    OpportunityBuildingView,
    OpportunityClient,
    OpportunityCompanyView,
    OpportunityContactView,
    OpportunityManualView,
    OpportunityMapResult,
    OpportunityUserView,
    OpportunityView,
    SearchOpportunitiesCalendarParameters,
    SearchOpportunitiesCalendarResult,
    SearchOpportunitiesMapParameters,
    SearchOpportunitiesParameters,
    SearchOpportunitiesResultPaginatedResultDto
} from './api-proxies';

@Injectable({
    providedIn: 'root'
})
export class OpportunityService implements IGridDataService {
    readonly opportunityContacts = computed<OpportunityContactView[]>(
        () => this._opportunity()?.opportunityContacts ?? []
    );

    readonly opportunityCompanies = computed<OpportunityCompanyView[]>(
        () => this._opportunity()?.opportunityCompanies ?? []
    );

    readonly opportunityUsers = computed<OpportunityUserView[]>(() => this._opportunity()?.opportunityUsers ?? []);

    readonly mainBuildingId = computed(() => this._opportunity()?.buildingId ?? null);

    readonly opportunity: Signal<OpportunityView | null>;
    readonly availableActions: Signal<number[]>;

    private readonly _opportunity: WritableSignal<OpportunityView | null>;
    private readonly _availableActions: WritableSignal<number[]>;
    private readonly client = inject(OpportunityClient);
    private readonly tabCounterService = inject(TabCounterService);

    constructor() {
        this._opportunity = signal<OpportunityView | null>(null);
        this.opportunity = this._opportunity.asReadonly();
        this._availableActions = signal<number[]>([]);
        this.availableActions = this._availableActions.asReadonly();
    }

    async setOpportunity(opportunity: OpportunityView | null): Promise<void> {
        this._opportunity.set(opportunity);
        if (opportunity?.id) {
            await this.loadAvailableActions(opportunity.id);
        } else {
            this._availableActions.set([]);
        }
    }

    async loadOpportunity(id: number): Promise<OpportunityView> {
        const opportunity = await this.getById(id);
        this._opportunity.set(opportunity);
        return opportunity;
    }

    updateOpportunityContacts(contacts: OpportunityContactView[]): void {
        const current = this._opportunity();
        if (!current) return;

        this._opportunity.set({
            ...current,
            opportunityContacts: contacts
        });
    }

    updateOpportunityCompanies(companies: OpportunityCompanyView[]): void {
        const current = this._opportunity();
        if (!current) return;

        this._opportunity.set({
            ...current,
            opportunityCompanies: companies
        });
    }

    updateOpportunityUsers(users: OpportunityUserView[]): void {
        const current = this._opportunity();
        if (!current) return;

        this._opportunity.set({
            ...current,
            opportunityUsers: users
        });
    }

    setMainBuilding(mainBuilding: OpportunityBuildingView | null): void {
        const current = this._opportunity();
        if (!current) return;

        if (!mainBuilding) {
            this._opportunity.set({
                ...current,
                buildingId: undefined,
                buildingName: undefined,
                buildingIsActive: undefined,
                buildingAddressId: undefined,
                line1: undefined,
                line2: undefined,
                addressLines: undefined,
                zipCode: undefined,
                countryName: undefined,
                stateName: undefined,
                cityName: undefined,
                fullAddress: undefined
            });
            return;
        }

        this._opportunity.set({
            ...current,
            buildingId: mainBuilding.buildingId,
            buildingName: mainBuilding.buildingName,
            buildingIsActive: mainBuilding.isActive,
            line1: mainBuilding.line1,
            line2: mainBuilding.line2,
            addressLines: mainBuilding.addressLines,
            zipCode: mainBuilding.zipCode,
            countryName: mainBuilding.countryName,
            stateName: mainBuilding.stateName,
            cityName: mainBuilding.cityName
        });
    }

    async updateTabCounters(entityTypeId: EntityTypes, entityId: number, tabCounters: TabCounterType): Promise<void> {
        await this.tabCounterService.updateCounters(entityTypeId, entityId, tabCounters);
    }

    async getById(id: number, forceReload = false): Promise<OpportunityView> {
        const current = this._opportunity();
        if (!forceReload && current && current.id === id) {
            return current;
        }

        const loaded = await lastValueFrom(this.client.opportunityGetById(id));
        this._opportunity.set(loaded);
        await this.loadAvailableActions(id);
        return loaded;
    }

    async getActualValues(id: number): Promise<GetOpportunityActualValuesResultDto> {
        return await lastValueFrom(this.client.opportunityGetActualValues(id));
    }

    async getTerm(id: number): Promise<GetOpportunityTermResultDto> {
        return await lastValueFrom(this.client.opportunityGetTerm(id));
    }

    async search(
        body: SearchOpportunitiesParameters | undefined
    ): Promise<SearchOpportunitiesResultPaginatedResultDto> {
        return await lastValueFrom(this.client.opportunitySearch(body));
    }

    async searchCalendar(body: SearchOpportunitiesCalendarParameters): Promise<SearchOpportunitiesCalendarResult[]> {
        return await lastValueFrom(this.client.opportunitySearchCalendar(body));
    }

    async searchMap(body: SearchOpportunitiesMapParameters): Promise<OpportunityMapResult[]> {
        return await lastValueFrom(this.client.opportunityMapSearch(body));
    }

    async saveUser(body: OpportunityUserView): Promise<OpportunityUserView> {
        return await lastValueFrom(this.client.opportunitySaveUser(body));
    }

    async saveCompany(body: OpportunityCompanyView): Promise<OpportunityCompanyView> {
        return await lastValueFrom(this.client.opportunitySaveCompany(body));
    }

    async saveContact(body: OpportunityContactView): Promise<OpportunityContactView> {
        return await lastValueFrom(this.client.opportunitySaveContact(body));
    }

    async setAsFavorite(body: number): Promise<void> {
        return await lastValueFrom(this.client.opportunitySetAsFavorite(body));
    }

    async removeFromFavorite(body: number): Promise<void> {
        return await lastValueFrom(this.client.opportunityRemoveFromFavorite(body));
    }

    async save(body: OpportunityView): Promise<OpportunityView> {
        const saved = await lastValueFrom(this.client.opportunitySave(body));
        const current = this._opportunity();
        if (current && current.id === saved.id) {
            this._opportunity.set(saved);
            await this.loadAvailableActions(saved.id);
        }
        return saved;
    }

    async validateForEstimateCreation(id: number): Promise<DomainValidationResult[]> {
        return await lastValueFrom(this.client.opportunityValidateForEstimateCreation(id));
    }

    async fetchAvailableActions(id: number): Promise<number[]> {
        return await lastValueFrom(this.client.opportunityAvailableActions(id));
    }

    async deactivate(id: number): Promise<void> {
        return await lastValueFrom(this.client.opportunityDeactivate(id));
    }

    async activate(id: number): Promise<void> {
        return await lastValueFrom(this.client.opportunityActivate(id));
    }

    async actualValues(id: number): Promise<GetOpportunityActualValuesResultDto> {
        return await lastValueFrom(this.client.opportunityGetActualValues(id));
    }

    async saveOpportunityManual(body: CreateOpportunityManualParameters): Promise<OpportunityManualView | undefined> {
        return await lastValueFrom(this.client.opportunitySaveOpportunityManual(body));
    }

    async edit(id: number, body: EditOpportunityView): Promise<void> {
        await lastValueFrom(this.client.opportunityEdit(id, body));
    }

    async setDisqualified(id: number): Promise<ChangeOpportunityStatusResultDto> {
        return await lastValueFrom(this.client.opportunityDisqualified(id));
    }

    async setUnqualified(id: number): Promise<ChangeOpportunityStatusResultDto> {
        return await lastValueFrom(this.client.opportunityUnqualified(id));
    }

    async loadAvailableActions(id: number): Promise<void> {
        const actions = await this.fetchAvailableActions(id);
        this._availableActions.set(actions);
    }
}
